import { gql } from '@apollo/client';

/* ── goals ──────────────────────────────────────────────────── */

export const GET_GOALS = gql`
  query GetGoals {
    getGoals(includeAccountData: true, includePolicyPortfolios: true, includeActionItems: true, usePrecomputed: true) {
      goals {
        goalId
        goalName
        displayName
        goalType
        goalStatus
        associatedPortfolioId
        goalTarget
        timeHorizon
        monthlyContribution
        startingValue
        accountData {
          totalAccounts
          associatedAccounts { accountId accountName securitySymbol marketValue accountType }
        }
        assetAllocation { equityAllocation bondAllocation expectedReturn successProbability riskLevel }
        actionItems { text type priority }
        progress {
          targetValue currentValue monthlyContribution fundedStatus status
          cumulativeGains cumulativeContributions totalAssets investmentReturns
          asOfDate shortfallVsPlan shortfallFlag
        }
        feasibility { isFeasible probOfMeetingTarget meanGapToTarget }
        # The policy portfolio the backend provisions with the goal, and the
        # model it was built from — the IPS reads both. Selected here because
        # includePolicyPortfolios: true above asks for them already.
        policyPortfolio {
          portfolioId portfolioName securitySymbol policyId policyName
          allocations { equityAllocation bondAllocation expectedReturn successProbability riskLevel }
          modelPortfolio { id name riskType assetClassModelId }
        }
        recommendedModel {
          assetClassModelId modelName modelStandardName riskPreference timeHorizonYears variantId
        }
      }
    }
  }
`;

/* ── goal planning options ──────────────────────────────────── */

/**
 * Every choice the goal planner offers, as the backend publishes it — the same
 * enum queries the app's goal flows read, so a new risk band or loan type shows
 * up here without a release. One document: the planner needs all of them at
 * once and none is worth a round trip of its own.
 *
 * `value` is what gets sent; `displayValue` is what is shown. Two lists do
 * not send `value` verbatim (property types and frequencies),
 * and `config/goalTypes.js` converts each where it builds the input.
 */
export const GET_GOAL_OPTIONS = gql`
  query GetGoalOptions {
    riskProfiles: getRiskProfileEnum(language: "en") { value displayValue description }
    programTypes: getEducationProgramTypeEnum(language: "en") { value displayValue }
    propertyTypes: getPropertyTypeEnum(language: "en") { value displayValue }
    frequencies: getFrequencySimplifiedTypeEnum(language: "en") { value displayValue }
    loanTypes: getLoanTypes {
      loanTypes {
        key
        displayName
        description
        typicalRateMinPct
        typicalRateMaxPct
        typicalTenureYearsMin
        typicalTenureYearsMax
        isEmi
      }
    }
  }
`;

/**
 * The goals a client is actually running, with the figures their committed plan
 * carries.
 *
 * `getGoals` answers from the precomputed goal record, which does not reflect a
 * plan committed minutes ago: target, contribution and model all read empty
 * until the nightly job has run. `getActiveGoals` reads the plan itself, which
 * is why the app's own goal screens use it — so a goal set here shows its
 * target immediately, and reopening the planner starts from what the goal holds.
 *
 * Scoped to one client through `X-Target-User-Id`, like every other goal read.
 */
export const GET_ACTIVE_GOALS = gql`
  query GetActiveGoals {
    getActiveGoals {
      goals {
        goalId
        goalName
        displayName
        recommendedModelId
        recommendedModelVariantId
        recommendedModelStandardName
        goalParameters {
          targetAmount
          initialCapitalContribution
          monthlyContribution
          currentSavings
          monthsToTarget
          riskTolerance
          contributionFrequency
          targetYear
          beneficiaryName
          beneficiaryAge
          currentAge
          plannedRetirementAge
          monthlyExpenses
          expectedPensionIncomeInRetirement
          targetAmountInr
          educationType
          expectedPurchasePrice
          expectedDownpaymentPercent
          downPaymentPercent
          propertyType
          currentFund
          monthsCoverage
          travelCost
          monthsToTravel
          targetGrams
          projectedGrams
          currentGrams
          goldPricePerGram
          outstandingAmount
          interestRate
          currentEmi
          originalMonthsRemaining
          monthsToClose
          targetPayoffMonths
          loanType
          debtName
        }
      }
      total
    }
  }
`;
